import type {
  ApolloClient,
  OperationVariables,
  Unmasked,
} from "@apollo/client";
import type { DocumentNode, TypedDocumentNode } from "@apollo/client/core";
import type { QueryHookOptions } from "@apollo/client/react";
import { skipToken, useApolloClient, useQuery } from "@apollo/client/react";
import type { DeepPartial } from "@apollo/client/utilities";
import { print } from "graphql";

import { generateGraphQLJWT } from "@/graphql/jwt-generator";

type OwnQueryResult<TData, TVariables extends OperationVariables> = ReturnType<
  typeof useQuery<TData, TVariables>
> & {
  setOptimistic: (data: DeepPartial<Unmasked<TData>>) => () => void;
  fetchOnly: () => Promise<{ data: TData }>;
  readLatestData: () => TData;
};

export const useOwnQuery = <
  TData = never,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): OwnQueryResult<TData, TVariables> => {
  const client = useApolloClient();
  const returnValue = useQuery<TData, TVariables>(query, options ?? skipToken);

  if (returnValue.error) {
    console.error({ error: returnValue.error });
  }

  return {
    ...returnValue,
    setOptimistic(data) {
      client.writeQuery({
        query,
        data,
        variables: options?.variables as TVariables,
      } as ApolloClient.WriteQueryOptions<
        DeepPartial<Unmasked<TData>>,
        TVariables
      >);
      // revert fn
      return () => {
        client.writeQuery({
          query,
          data: returnValue.data as Unmasked<TData>,
          variables: options?.variables as TVariables,
        } as ApolloClient.WriteQueryOptions<Unmasked<TData>, TVariables>);
      };
    },
    async fetchOnly() {
      const token = await generateGraphQLJWT();

      return fetch(process.env.NEXT_PUBLIC_GRAPHQL_URI, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          query: print(query),
          variables: options?.variables,
        }),
      }).then((res) => res.json());
    },
    readLatestData: () => {
      return client.readQuery<TData>({
        query,
        variables: options?.variables,
      }) as TData;
    },
  } as OwnQueryResult<TData, TVariables>;
};
